import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const BusinessHours = () => {
  const [businessHours, setBusinessHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Day names mapping (0 = Sunday, 1 = Monday, etc.)
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    fetchBusinessHours();
  }, []);

  const fetchBusinessHours = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('clinic_business_hours')
        .select('*')
        .order('day_of_week');

      if (fetchError) throw fetchError;

      console.log('📅 Fetched business hours from database:', data);

      // Get current day (0 = Sunday, 1 = Monday, etc.)
      const currentDay = new Date().getDay();

      // Transform database data to component format
      const formattedHours = data.map(schedule => {
        const dayName = dayNames[schedule.day_of_week];
        const isToday = schedule.day_of_week === currentDay;

        // Format time from "09:00:00" to "9:00 AM"
        const formatTime = (timeStr) => {
          if (!timeStr) return '';
          const [hours, minutes] = timeStr.split(':');
          const hour = parseInt(hours);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
          return `${displayHour}:${minutes} ${ampm}`;
        };

        // Build hours string with break time if exists
        let hoursDisplay;
        if (!schedule.is_open) {
          hoursDisplay = 'Closed';
        } else {
          // Use correct column names: opening_time and closing_time (not open_time/close_time)
          const mainHours = `${formatTime(schedule.opening_time)} - ${formatTime(schedule.closing_time)}`;

          // Add break hours if they exist
          if (schedule.break_start_time && schedule.break_end_time) {
            const breakHours = `Break: ${formatTime(schedule.break_start_time)} - ${formatTime(schedule.break_end_time)}`;
            hoursDisplay = `${mainHours} (${breakHours})`;
          } else {
            hoursDisplay = mainHours;
          }
        }

        return {
          day: dayName,
          hours: hoursDisplay,
          isToday: isToday,
          isOpen: schedule.is_open,
          // Store raw times for status calculation
          rawOpeningTime: schedule.opening_time,
          rawClosingTime: schedule.closing_time,
          rawBreakStart: schedule.break_start_time,
          rawBreakEnd: schedule.break_end_time
        };
      });

      console.log('✅ Formatted business hours:', formattedHours);
      setBusinessHours(formattedHours);
    } catch (err) {
      console.error('❌ Error fetching business hours:', err);
      setError('Failed to load business hours');

      // Fallback to default hours if fetch fails
      const currentDay = new Date().getDay();
      setBusinessHours(dayNames.map((day, index) => ({
        day: day,
        hours: index === 0 ? 'Closed' : '9:00 AM - 7:00 PM', // Sunday closed
        isToday: index === currentDay,
        isOpen: index !== 0, // Sunday is closed
        rawOpeningTime: '09:00:00',
        rawClosingTime: '19:00:00',
        rawBreakStart: null,
        rawBreakEnd: null
      })));
    } finally {
      setLoading(false);
    }
  };

  const specialServices = [

    {
      icon: "Clock",
      title: "Extended Hours",
      description: "Late appointments available by request",
      color: "text-primary"
    },
    {
      icon: "Calendar",
      title: "Holiday Schedule",
      description: "Special hours during holidays - call ahead",
      color: "text-accent"
    }
  ];

  const getCurrentStatus = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTotalMinutes = currentHour * 60 + currentMinutes;

    console.log('🕐 Current time:', `${currentHour}:${currentMinutes}`, `(${currentTotalMinutes} minutes)`);

    // Find today's schedule
    const todaySchedule = businessHours.find(schedule => schedule.isToday);

    console.log('📋 Today\'s schedule:', todaySchedule);

    if (!todaySchedule || !todaySchedule.isOpen) {
      console.log('❌ Clinic is closed today');
      return { isOpen: false, message: "Currently Closed" };
    }

    // Parse database time format "09:00:00" or "09:00" to minutes since midnight
    const parseDbTime = (timeStr) => {
      if (!timeStr) return null;
      const parts = timeStr.split(':');
      const hours = parseInt(parts[0]);
      const minutes = parseInt(parts[1]);
      return hours * 60 + minutes;
    };

    const openMinutes = parseDbTime(todaySchedule.rawOpeningTime);
    const closeMinutes = parseDbTime(todaySchedule.rawClosingTime);

    console.log('⏰ Opening time:', todaySchedule.rawOpeningTime, `(${openMinutes} minutes)`);
    console.log('⏰ Closing time:', todaySchedule.rawClosingTime, `(${closeMinutes} minutes)`);

    // Check if we're in break time
    if (todaySchedule.rawBreakStart && todaySchedule.rawBreakEnd) {
      const breakStartMinutes = parseDbTime(todaySchedule.rawBreakStart);
      const breakEndMinutes = parseDbTime(todaySchedule.rawBreakEnd);

      console.log('☕ Break time:', `${todaySchedule.rawBreakStart} - ${todaySchedule.rawBreakEnd}`, `(${breakStartMinutes} - ${breakEndMinutes} minutes)`);

      if (currentTotalMinutes >= breakStartMinutes && currentTotalMinutes < breakEndMinutes) {
        console.log('☕ Currently in break time');
        return { isOpen: false, message: "Currently on Break" };
      }
    }

    // Check if we're within business hours
    if (openMinutes !== null && closeMinutes !== null) {
      if (currentTotalMinutes >= openMinutes && currentTotalMinutes < closeMinutes) {
        console.log('✅ Clinic is OPEN!');
        return { isOpen: true, message: "We're Open Now!" };
      }
    }

    console.log('❌ Outside business hours');
    return { isOpen: false, message: "Currently Closed" };
  };

  const status = getCurrentStatus();

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="font-headline text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Business Hours
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We're here to serve you with flexible scheduling and extended hours for your convenience.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Hours Schedule */}
          <div className="space-y-6">
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-headline text-2xl font-semibold text-foreground">
                  Weekly Schedule
                </h3>
                <div className={`flex items-center space-x-2 px-4 py-2 rounded-full ${status?.isOpen ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                  }`}>
                  <div className={`w-2 h-2 rounded-full ${status?.isOpen ? 'bg-success animate-pulse' : 'bg-muted-foreground'
                    }`}></div>
                  <span className="text-sm font-medium">{status?.message}</span>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Icon name="Loader2" size={24} className="text-primary animate-spin" />
                  <span className="ml-2 text-muted-foreground">Loading hours...</span>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center py-8 text-error">
                  <Icon name="AlertCircle" size={20} className="mr-2" />
                  <span>{error}</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {businessHours?.map((schedule, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-4 rounded-lg transition-all duration-300 ${schedule?.isToday
                        ? 'glass-panel border border-primary/20 morph-hover' : 'hover:bg-muted/50'
                        }`}
                    >
                      <div className="flex items-center space-x-3">
                        {schedule?.isToday && (
                          <Icon name="Calendar" size={16} className="text-primary" />
                        )}
                        <span className={`font-medium ${schedule?.isToday ? 'text-primary' : 'text-foreground'
                          }`}>
                          {schedule?.day}
                          {schedule?.isToday && <span className="text-xs ml-2">(Today)</span>}
                        </span>
                      </div>
                      <span className="text-muted-foreground">{schedule?.hours}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => window.location.href = '/booking'}
                className="glass-card p-4 interactive-card group"
              >
                <div className="text-center">
                  <Icon name="Calendar" size={24} className="text-primary mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="font-medium text-foreground">Book Now</p>
                  <p className="text-xs text-muted-foreground">Online scheduling</p>
                </div>
              </button>

              <button
                onClick={() => window.location.href = 'tel:+917842202255'}
                className="glass-card p-4 interactive-card group"
              >
                <div className="text-center">
                  <Icon name="Phone" size={24} className="text-success mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="font-medium text-foreground">Call Us</p>
                  <p className="text-xs text-muted-foreground">Speak with expert</p>
                </div>
              </button>
            </div>
          </div>

          {/* Special Services */}
          <div className="space-y-6">
            <div className="glass-card p-6">
              <h3 className="font-headline text-2xl font-semibold text-foreground mb-6">
                Special Services
              </h3>

              <div className="space-y-4">
                {specialServices?.map((service, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 rounded-lg hover:bg-muted/30 transition-colors duration-300">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${service?.color} bg-current/10`}>
                      <Icon name={service?.icon} size={18} className={service?.color} />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-1">{service?.title}</h4>
                      <p className="text-sm text-muted-foreground">{service?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Important Notes */}
            <div className="glass-card p-6">
              <h3 className="font-headline text-xl font-semibold text-foreground mb-4">
                Important Notes
              </h3>

              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Last appointments are scheduled 15 minutes before closing time
                  </p>
                </div>

                <div className="flex items-start space-x-3">
                  <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    24-hour cancellation policy applies to all appointments
                  </p>
                </div>

                <div className="flex items-start space-x-3">
                  <Icon name="Heart" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Walk-ins welcome based on availability
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessHours;